import { apiSlice } from './apiSlice';
import { NotificationActions } from './notificationSlice';

export const organizationSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getOrganization: builder.query({
            query: (organizationId) => `/organizations/${organizationId}`,
            providesTags: (result, error, arg) => [{ type: 'Organization', id: arg }]
        }),
        getOrganizationUsers: builder.query({
            query: (organizationId) => `/organizations/${organizationId}/users`,
            providesTags: (result = []) =>
                result.data ?
                    [
                        ...result.data.map(({ id }) => ({ type: 'User', id })),
                        { type: 'User', id: 'PARTIAL-LIST' }
                    ] :
                    [{ type: 'User', id: 'PARTIAL-LIST' }],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch {
                    dispatch(NotificationActions.addErrorNotification({ 
                        message: 'Could not load users at this time. Please try again later.' 
                    }));
                }  
            },
        }),
        getHospitalsInOrganization: builder.query({
            query: (organizationId) => `/organizations/${organizationId}/hospitals`,
            providesTags: (result, error, arg) => [{ type: 'Hospital', id: arg }]
        }),
        getOrganizationCardOwners: builder.query({
            query: (organizationId) => `/organizations/${organizationId}/cardOwners`,
            providesTags: (result, error, arg) => [{ type: 'Physician', id: arg }]
        }),
        searchCardOwners: builder.query({
            query: ({ organizationId, searchTerm, filter, page }) => ({
                url: `/organizations/${organizationId}/cardOwnerSearch${page ? `?page=${page}` : ''}${searchTerm ? `&search=${searchTerm}` : ''}${filter ? `&specialty=${filter}` : ''}`,
            }),
            providesTags: (result = [], error, arg) => result.data
                ? [
                    ...result.data.map(({ id }) => ({ type: 'CardOwner', id })),
                    { type: 'CardOwner', id: 'PARTIAL-LIST' },
                ]
                : [{ type: 'CardOwner', id: 'PARTIAL-LIST' }],
        }),
        getItemUsageStatsByGroup: builder.query({
            query: ({ organizationId, itemId, groupBy, startDate, endDate, hospitalIds, granularity }) => {
                const params = new URLSearchParams();
                if (startDate) params.append('startDate', startDate);
                if (endDate) params.append('endDate', endDate);
                if (hospitalIds) params.append('hospitalIds', hospitalIds);
                if (granularity) params.append('granularity', granularity);

                return {
                    url: `/organizations/${organizationId}/items/${itemId}/usageStats/${groupBy}`,
                    params: Object.fromEntries(params)
                };
            },
            transformResponse: (response) => response.data,
            providesTags: (result, error, { organizationId, itemId, groupBy, startDate, endDate, granularity }) => [
                { type: 'ItemUsageStats', id: `${organizationId}-${itemId}-${groupBy}-${startDate}-${endDate}-${granularity ?? ''}` }
            ]
        }),
        uploadPhysicianPhoneNumberFile: builder.mutation({
            query: ({ data, organizationId }) => ({
                url: `/organizations/${organizationId}/phoneNumberUpload`,
                method: 'POST',
                body: data,
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(NotificationActions.addSuccessNotification({
                        message: 'Successfully uploaded physician phone numbers.'
                    }));
                } catch (error) {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Error occurred while uploading physician phone numbers. Please double check your file and try again later.'
                    }));
                }
            }
        }),
        generateUncategorizedItemsReport: builder.mutation({
            query: ({ data, organizationId }) => ({
                url: `/organizations/${organizationId}/itemCategoriesReport`,
                method: 'POST',
                body: data,
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(NotificationActions.addSuccessNotification({
                        message: 'Report request accepted. You will receive the uncategorized items report by email shortly.'
                    }));
                } catch (error) {
                    if (error.status === 401) {
                        dispatch(NotificationActions.addErrorNotification({
                            message: 'You do not have permission to generate this report.'
                        }));
                    } else if (error.status === 400) {
                        dispatch(NotificationActions.addErrorNotification({
                            message: 'A valid email address is required on your account before this report can be generated.'
                        }));
                    } else {
                        dispatch(NotificationActions.addErrorNotification({
                            message: 'Error occurred while generating the uncategorized items report. Please try again later.'
                        }));
                    }
                }
            }
        }),
        getUploadedFilesInOrganization: builder.query({
            query: (arg) => {
                // Handle both string organizationId and object { organizationId, month }
                if (typeof arg === 'string') {
                    return `/organizations/${arg}/sourceFiles`;
                }
                
                const { organizationId, month } = arg;
                const params = new URLSearchParams();
                if (month) {
                    params.append('month', month);
                }
                
                return {
                    url: `/organizations/${organizationId}/sourceFiles`,
                    params: params.toString() ? Object.fromEntries(params) : undefined
                };
            },
            providesTags: (result, error, arg) => {
                const id = typeof arg === 'string' ? arg : arg.organizationId;
                return [{ type: 'SourceFileUploads', id }];
            }
        }),
        getSourceFileUploadHistoryMonths: builder.query({
            query: (organizationId) => `/organizations/${organizationId}/sourceFiles/history`,
            transformResponse: (response) => {
                if (Array.isArray(response?.history_dates)) {
                    return response.history_dates
                        .map((entry) => {
                            if (typeof entry === 'string') {
                                return entry;
                            }
                            return entry?.month;
                        })
                        .filter((month) => typeof month === 'string');
                }

                return [];
            }
        }),
        getAugmentationFilesInOrganization: builder.query({
            query: (organizationId) => `/organizations/${organizationId}/augmentationFiles`,
            providesTags: (result, error, arg) => [{ type: 'AugmentationFileUploads', id: arg }]
        }),
        getAugmentationFileDownloadPath: builder.query({
            query: ({ organizationId, fileName }) => ({
                url: `/organizations/${organizationId}/augmentationFiles/download`,
                params: { fileName }
            })
        }),
        getFileHeaders: builder.query({
            query: ({ organizationId, fileType, fileName, historyDate }) => ({
                url: `/organizations/${organizationId}/files/${fileType}/headers`,
                params: {
                    fileName,
                    ...(historyDate ? { historyDate } : {})
                }
            }),
            providesTags: (result, error, { organizationId, fileType, fileName, historyDate }) => [
                { type: 'FileHeaders', id: `${organizationId}-${fileType}-${fileName}-${historyDate || 'current'}` }
            ]
        }),
        getFileMetadata: builder.query({
            query: ({ organizationId, fileType, fileName, historyDate }) => ({
                url: `/organizations/${organizationId}/files/${fileType}/metadata`,
                params: {
                    fileName,
                    ...(historyDate ? { historyDate } : {})
                }
            }),
            providesTags: (result, error, { organizationId, fileType, fileName, historyDate }) => [
                { type: 'FileMetadata', id: `${organizationId}-${fileType}-${fileName}-${historyDate || 'current'}` }
            ]
        }),
        uploadSourceFile: builder.mutation({
            query: ({ data, organizationId }) => ({
                url: `/organizations/${organizationId}/sourceFiles`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'SourceFileUploads', id: arg.organizationId }],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(NotificationActions.addSuccessNotification({
                        message: 'Successfully uploaded source file.'
                    }));
                } catch (error) {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Error occurred while uploading source file. Please double check your file and try again later.'
                    }));
                }
            }
        }),
        uploadAugmentationFile: builder.mutation({
            query: ({ data, organizationId }) => ({
                url: `/organizations/${organizationId}/augmentationFiles`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'AugmentationFileUploads', id: arg.organizationId }],
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(NotificationActions.addSuccessNotification({
                        message: 'Successfully uploaded augmentation file.'
                    }));
                } catch (error) {
                    dispatch(NotificationActions.addErrorNotification({
                        message: 'Error occurred while uploading augmentation file. Please double check your file and try again later.'
                    }));
                }
            }
        }),
        getTransformationJobs: builder.query({
            query: ({ organizationId, maxResults, nextToken }) => {
                const params = new URLSearchParams();
                if (maxResults) {
                    params.append('maxResults', maxResults);
                }
                if (nextToken) {
                    params.append('nextToken', nextToken);
                }
                
                return {
                    url: `/organizations/${organizationId}/transformationJobs`,
                    params: params.toString() ? Object.fromEntries(params) : undefined
                };
            },
            providesTags: (result, error, { organizationId, nextToken }) => [
                { 
                    type: 'TransformationJobs', 
                    id: `${organizationId}-${nextToken || 'initial'}` 
                }
            ]
        }),
    }),
});

export const {
    useGetOrganizationQuery,
    useGetOrganizationUsersQuery,
    useGetHospitalsInOrganizationQuery,
    useGetOrganizationCardOwnersQuery,
    useSearchCardOwnersQuery,
    useUploadPhysicianPhoneNumberFileMutation,
    useGenerateUncategorizedItemsReportMutation,
    useGetUploadedFilesInOrganizationQuery,
    useGetSourceFileUploadHistoryMonthsQuery,
    useGetAugmentationFilesInOrganizationQuery,
    useLazyGetAugmentationFileDownloadPathQuery,
    useGetFileHeadersQuery,
    useGetFileMetadataQuery,
    useUploadSourceFileMutation,
    useUploadAugmentationFileMutation,
    useGetTransformationJobsQuery,
    useGetItemUsageStatsByGroupQuery,
} = organizationSlice;
